import React, { useState } from "react";
import { useFetchAuditTrailsQuery } from "api/api-audittrail";
import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Grid, Paper, Stack, Table, TableBody, TableContainer, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAppSelector } from "redux/hooks";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import SyncIcon from "@mui/icons-material/Sync";

interface ChangeDetail {
  column_name: string;
  old_value: string | null;
  new_value: string | null;
}

interface ChangeDetail {
  column_name: string;
  old_value: string | null;
  new_value: string | null;
}

const LogsTable: React.FC<{ data: ChangeDetail[] }> = ({ data = [] }) => {

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 400 }} aria-label="simple table">
        <TableBody>
          <Stack spacing={2} sx={{ flexFlow: "nowrap" }}>
            {data.map((col) => (
              <Box sx={{ p: 2, paddingTop: "0px !important", marginTop: "0px !important" }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {col.column_name}
                </Typography>
                <Divider />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Old Value:
                  </Typography>
                  <Typography variant="body1">
                    {col.old_value ?? <em>Null</em>}
                  </Typography>
                </Box>
                <Box mt={1}>
                  <Typography variant="body2" color="text.secondary">
                    New Value:
                  </Typography>
                  <Typography variant="body1">
                    {col.new_value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const AuditTrailViewer: React.FC<{ recordIdFilter?: string }> = ({ recordIdFilter = "" }) => {
  const [visible, setVisible] = useState(false);
  const { data: auditData = [], isLoading, refetch } = useFetchAuditTrailsQuery(recordIdFilter ?? "", {
    skip: !visible || !recordIdFilter,
  });

  const handleClick = () => {
    setVisible(!visible);
    if (!visible) {
      refetch();
    }
  };

  const USER_ROLE = useAppSelector((state) => state.appState.role);
  const { data: curr_user } = useGetCurrentUserQuery();
  const isInternal = curr_user?.is_internal;
  const isInternalUser = USER_ROLE?.role === "internal" || USER_ROLE?.role === "admin" || isInternal;

  return (
    <>
      {isInternalUser && (
        <Grid container item spacing={2} sx={{ marginBottom: "10px" }}>
          <Grid item mx={1}>
            <Typography
              sx={{
                fontWeight: "600",
                cursor: "pointer",
                fontSize: "1.1em",
              }}
              onClick={handleClick}
            >
              {visible ? "Audit Logs" : "Audit Logs"}
            </Typography>
          </Grid>

          <Grid item>
            {visible && (
              <SyncIcon
                onClick={!isLoading ? () => refetch() : undefined}
                sx={{ ":hover": { cursor: "pointer", paddingTop: "18px" } }}
                titleAccess="Sync latest changes"
              />
            )}
          </Grid>

          {visible && (
            <Grid item sx={{ maxWidth: "100%", minHeight: "30px" }}>
              <Box className="audit-list">
                {isLoading && <p>Loading...</p>}
                {!isLoading && auditData.length === 0 && (
                  <p>No audit data available</p>
                )}
                {!isLoading && auditData.length > 0 && (
                  <Box className="audit-group">
                    {auditData.map((entry) => (
                      <Accordion sx={{ margin: "20px 0", minHeight: "30px" }}>
                        <AccordionSummary
                          sx={{ minHeight: "26px" }}
                          expandIcon={<ExpandMoreIcon />}
                        >
                          <Typography component="p">
                            <strong>{entry.operation}</strong> on{" "}
                            {new Date(entry.date).toLocaleString()} by {entry.user_name}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ paddingTop: 0 }}>
                          <LogsTable data={entry.details} />
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default AuditTrailViewer;
